<template>
    <div
        :class="['dt-color-radio', { error: isError }]"
        :id="id"
        :required="required"
    >
        <input
            v-for="(option, index) in options"
            :key="index"
            type="radio"
            class="dt-color-radio-option"
            :style="{ backgroundColor: option.value }"
            :data-payload="fullPayload"
            :value="option.value"
            v-model="selected"
            @change="
                saveToLocalStorage();
                isError = false;
            "
        />
    </div>
</template>

<script>
import EventBus from "../event-bus";
import { safeGetJson, safeSetJson } from "../utils/safeStorage";

export default {
    name: "DTColorRadio",
    props: {
        id: {
            type: String,
            default: "",
        },
        payload: {
            type: String,
            required: true,
        },
        sharePayload: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            required: true,
            default: () => [],
        },
        required: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isError: false,
            fullPayload: this.payload,
            selected: "",
        };
    },
    inject: ["pageSettings"],
    mounted() {
        for (let i = 0; i < this.options.length; i++) {
            if (this.options[i].selected) {
                this.selected = this.options[i].value;
                break;
            }
        }

        this.$nextTick(() => {
            EventBus.on("throwErrorForRequiredInputs", this.handleErrorState);
            this.fullPayload = this.getFullPayload();
            this.loadFromLocalStorage();
        });
    },
    beforeUnmount() {
        EventBus.off("throwErrorForRequiredInputs", this.handleErrorState);
    },
    methods: {
        getFullPayload() {
            if (!this.payload || this.payload.trim() === "") {
                return null;
            }

            let element = this.$el;

            while (element && element.parentElement) {
                if (element.parentElement.classList.contains("tab-content")) {
                    const prefix = element.parentElement.getAttribute(
                        "data-payload-prefix"
                    );
                    if (prefix) {
                        return `${prefix}.${this.payload}`;
                    }
                }
                element = element.parentElement;
            }

            return this.payload;
        },
        loadFromLocalStorage() {
            if (!this.fullPayload || !this.pageSettings.storageKey) {
                return;
            }

            const stepValues = safeGetJson(this.pageSettings.storageKey, {});
            const sharedValues = safeGetJson("sharedCustomizerValues", {});
            const keys = this.fullPayload.split(".");
            let current = stepValues;
            let sharedCurrent = sharedValues;

            for (let i = 0; i < keys.length; i++) {
                if (!current[keys[i]]) {
                    current[keys[i]] =
                        i === keys.length - 1
                            ? { value: "", required: this.required }
                            : {};
                }
                current = current[keys[i]];

                if (
                    this.sharePayload &&
                    sharedCurrent[keys[i]] === undefined &&
                    i < keys.length - 1
                ) {
                    sharedCurrent[keys[i]] = {};
                } else if (i === keys.length - 1 && this.sharePayload) {
                    sharedCurrent = sharedCurrent[keys[i]];
                } else if (sharedCurrent[keys[i]]) {
                    sharedCurrent = sharedCurrent[keys[i]];
                }
            }

            if (this.sharePayload && sharedCurrent && sharedCurrent.value) {
                this.selected = sharedCurrent.value;
                current.value = sharedCurrent.value;
            } else if (current.value) {
                this.selected = current.value;
            } else {
                current.value = this.selected || "";
            }

            safeSetJson(this.pageSettings.storageKey, stepValues);
            if (this.sharePayload) {
                safeSetJson("sharedCustomizerValues", sharedValues);
            }
        },
        saveToLocalStorage() {
            if (!this.fullPayload || !this.pageSettings.storageKey) {
                return;
            }

            const stepValues = safeGetJson(this.pageSettings.storageKey, {});
            const sharedValues = safeGetJson("sharedCustomizerValues", {});
            const keys = this.fullPayload.split(".");
            let currentStep = stepValues;
            let currentShared = sharedValues;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!currentStep[keys[i]]) {
                    currentStep[keys[i]] = {};
                }
                currentStep = currentStep[keys[i]];

                if (this.sharePayload) {
                    if (!currentShared[keys[i]]) {
                        currentShared[keys[i]] = {};
                    }
                    currentShared = currentShared[keys[i]];
                }
            }

            currentStep[keys[keys.length - 1]] = {
                ...(currentStep[keys[keys.length - 1]] || {}),
                value: this.selected,
                required: this.required,
            };

            if (this.sharePayload) {
                currentShared[keys[keys.length - 1]] = {
                    ...(currentShared[keys[keys.length - 1]] || {}),
                    value: this.selected,
                    required: this.required,
                };
            }

            safeSetJson(this.pageSettings.storageKey, stepValues);
            if (this.sharePayload) {
                safeSetJson("sharedCustomizerValues", sharedValues);
            }
        },
        handleErrorState(newState) {
            if (newState === false) {
                this.isError = false;
                return;
            }

            if (this.required && this.selected.trim() === "") {
                this.isError = true;
            }
        },
        setAIValue(newValue) {
            for (let i = 0; i < this.options.length; i++) {
                if (this.options[i].value === newValue) {
                    this.selected = newValue;
                    break;
                }
            }

            this.saveToLocalStorage();
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;
.dt-color-radio {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    column-gap: 8px;
    width: 100%;

    &.error {
        padding: 5px;
        border-radius: 8px;
        border: 2px solid $color-input-error;
    }

    .dt-color-radio-option {
        appearance: none;
        -webkit-appearance: none;
        position: relative;
        display: block;
        width: 32px;
        height: 32px;
        border: none;
        border-radius: 50%;
        margin: 0;
        cursor: pointer;

        &:checked {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.05);

            &::after {
                content: "";
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 32px;
                height: 32px;
                border-radius: 50%;
                border: 3px solid $color-new-black;
                background-color: transparent;
                box-sizing: content-box;
            }
        }
    }
}
</style>
